import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ListChecks, CheckCircle, XCircle, ArrowRight, X, Clock } from 'lucide-react';

const fmtT = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

export const VerifiedInterviewMCQ = ({ API, token, attemptId, onClose, timeLimit = 300 }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API}/api/verified-talent/attempts/${attemptId}/mcq/generate`, {}, { ...H, timeout: 90000 });
        setQuestions(res.data.questions || []);
        setTimeLeft(timeLimit);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load the MCQ round');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async (force = false) => {
    if (submitting) return;
    if (!force && Object.keys(answers).length < questions.length) {
      toast.info('Please answer all questions before submitting');
      return;
    }
    setSubmitting(true);
    setTimeLeft(null);
    try {
      const responses = questions.map((_, i) => (answers[i] ?? -1));
      const res = await axios.post(`${API}/api/verified-talent/attempts/${attemptId}/mcq/submit`, { responses }, { ...H, timeout: 60000 });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Countdown → auto-submit when time runs out
  useEffect(() => {
    if (timeLeft === null || result || loading) return;
    if (timeLeft <= 0) { if (!submitting) submit(true); return; }
    const id = setInterval(() => setTimeLeft((t) => (t === null ? t : t - 1)), 1000);
    return () => clearInterval(id);
  }, [timeLeft, result, loading, submitting]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" data-testid="mcq-modal">
      <div className="w-full max-w-2xl max-h-[90vh] rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-sky-400" />
            <span className="font-semibold text-white">Rapid Knowledge Check</span>
          </div>
          <div className="flex items-center gap-3">
            {timeLeft !== null && !result && (
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded ${timeLeft <= 30 ? 'bg-red-500/15 text-red-300' : 'bg-white/10 text-zinc-300'}`} data-testid="mcq-timer">
                <Clock size={12} /> {fmtT(timeLeft)}
              </span>
            )}
            {!result && (
              <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="mcq-close-btn" title="Close (optional)"><X size={18} /></button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-zinc-400 gap-2 py-16"><Loader2 className="animate-spin" size={18} /> Preparing questions…</div>
        ) : result ? (
          <div className="flex-1 overflow-auto p-6 space-y-5" data-testid="mcq-result">
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${result.score >= 60 ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
                <span className="text-2xl font-bold text-white">{result.score}%</span>
              </div>
              <h3 className="mt-3 text-xl font-bold text-white">{result.correct}/{result.total} correct</h3>
            </div>
            <div className="space-y-3">
              {(result.review || []).map((r, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-start gap-2">
                    {r.is_correct ? <CheckCircle size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" /> : <XCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-zinc-100">{r.question}</p>
                      {questions[i] && (
                        <p className="text-xs mt-1 text-zinc-400">Correct: <span className="text-emerald-300">{questions[i].options[r.correct_index]}</span></p>
                      )}
                      {r.explanation && <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{r.explanation}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center">
              <button onClick={onClose} className="inline-flex items-center gap-2 px-6 py-2.5 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-bold" data-testid="mcq-continue-btn">
                Continue interview <ArrowRight size={15} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-5 space-y-5">
            {questions.map((q, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`mcq-question-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <p className="text-sm font-semibold text-zinc-100">{i + 1}. {q.question}</p>
                  {q.skill && <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-zinc-500 border border-white/10 rounded-full px-2 py-0.5">{q.skill}</span>}
                </div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => (
                    <label key={oi} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer text-sm transition-colors ${answers[i] === oi ? 'border-sky-400/50 bg-sky-500/10 text-white' : 'border-white/10 text-zinc-300 hover:border-white/20'}`} data-testid={`mcq-${i}-option-${oi}`}>
                      <input type="radio" name={`q${i}`} checked={answers[i] === oi} onChange={() => setAnswers((p) => ({ ...p, [i]: oi }))} className="accent-sky-500" />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="text-center pt-1">
              <button onClick={() => submit()} disabled={submitting} className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="mcq-submit-btn">
                {submitting ? <Loader2 size={15} className="animate-spin" /> : null} Submit answers
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifiedInterviewMCQ;
